import React, { useEffect, useState } from "react";
import { getCurrencySymbol } from "../../../../../tenant/currency";
import PaymentMethodModal, { PaymentMethodKey, PaymentMethodRow } from "../../../components/payment-method-modal/PaymentMethodModal";
import TransactionPinModal from "../../../components/transaction-pin-modal/TransactionPinModal";
import CustomerPinModal from "../../../components/customer-pin-modal/CustomerPinModal";
import { Money } from "../../../../../model/common/money";
import { ApplicationRoutes } from "../../../../../routes";
import { toast } from "react-toastify";
import {
	SubmitServiceRequestPayload,
	useSubmitServiceRequestWithWalletMutation,
	useSubmitServiceRequestWithCardMutation,
} from "../../../../../service/customersupport-service";
import { useGetPaymentFeeMutation, useGetWalletBalanceQuery } from "../../../../../service/charge-service";

type ModalView = "method" | "pin" | "reset-pin";

interface ServiceRequestPaymentModalProps {
	price: Money;
	payload: SubmitServiceRequestPayload;
	onClose: () => void;
	onWalletPaid: (reference: string) => void;
	onCardInitiated: (data: { url: string; reference: string }) => void;
}

const currencySymbol = (code: string): string => getCurrencySymbol(code);

const fmt = (value: number, decimals = 0) =>
	value.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const ServiceRequestPaymentModal: React.FC<ServiceRequestPaymentModalProps> = ({
	price,
	payload,
	onClose,
	onWalletPaid,
	onCardInitiated,
}) => {
	const [view, setView] = useState<ModalView>("method");
	const [selectedMethod, setSelectedMethod] = useState<PaymentMethodKey | null>(null);
	const [fee, setFee] = useState(0);

	const [getPaymentFee] = useGetPaymentFeeMutation();
	const { data: wallet } = useGetWalletBalanceQuery();
	const [submitWithWallet, { isLoading: isWalletLoading }] = useSubmitServiceRequestWithWalletMutation();
	const [submitWithCard, { isLoading: isCardLoading }] = useSubmitServiceRequestWithCardMutation();

	useEffect(() => {
		if (!price.currency || price.amount <= 0) return;
		let active = true;
		getPaymentFee({ currency: price.currency, amount: price.amount })
			.unwrap()
			.then((res: any) => {
				if (active) setFee(res?.estimationFees?.[0]?.paymentChannels?.[0]?.clientFee?.amount ?? 0);
			})
			.catch(() => {
				if (active) setFee(0);
			});
		return () => {
			active = false;
		};
	}, [price, getPaymentFee]);

	const sym = currencySymbol(price.currency);
	const totalAmount = price.amount + fee;
	const walletBalance = wallet?.walletDetails?.[0]?.availableBalance ?? 0;
	const hasWallet = !!wallet?.walletDetails?.length;
	const isLoading = isWalletLoading || isCardLoading;

	const methods: PaymentMethodRow[] = [
		{ key: "card" },
		{ key: "transfer" },
		...(hasWallet ? [{ key: "wallet" as const, pill: { label: "Balance:", amount: `${sym}${fmt(walletBalance)}` } }] : []),
	];

	const handleMethodPay = (key: PaymentMethodKey) => {
		if (key === "wallet" && totalAmount > walletBalance) {
			const shortfall = totalAmount - walletBalance;
			toast.error(`Insufficient balance. You need ${sym}${fmt(shortfall, 2)} more.`);
			return;
		}
		setSelectedMethod(key);
		setView("pin");
	};

	const handlePinSubmit = async (pin: string) => {
		if (selectedMethod === "wallet") {
			const res = await submitWithWallet({ ...payload, pin }).unwrap();
			onWalletPaid(res.reference);
		} else {
			const returnUrl = `${window.location.origin}/${ApplicationRoutes.CUSTOMER_SERVICE_REQUEST_CREATE}`;
			const paymentTypes = selectedMethod === "transfer" ? ["OneTimeWallet"] : ["card"];
			const res = await submitWithCard({ ...payload, pin, returnUrl, paymentTypes }).unwrap();
			onCardInitiated({ url: res.url, reference: res.reference });
		}
	};

	if (view === "reset-pin") {
		return (
			<CustomerPinModal
				initialContent="resetPinContent"
				showResetSuccessScreen
				onClose={() => setView("pin")}
			/>
		);
	}

	if (view === "pin") {
		return (
			<TransactionPinModal
				submitText={`Pay ${sym}${fmt(totalAmount)}`}
				isSubmitting={isLoading}
				onClose={onClose}
				onSubmit={handlePinSubmit}
				onForgetPin={() => setView("reset-pin")}
			/>
		);
	}

	return (
		<PaymentMethodModal
			title="Payment"
			amountText={`${sym}${fmt(totalAmount)}`}
			methods={methods}
			payLabel={`Pay ${sym}${fmt(totalAmount)}`}
			onClose={onClose}
			onPay={handleMethodPay}
		/>
	);
};

export default ServiceRequestPaymentModal;
